import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { LOCALES } from '@exiplan/shared-types';

import en from './locales/en.json';
import fr from './locales/fr.json';

/**
 * Configuration i18next du client exiPlan — chantier i18n EN/FR du studio Exidis.
 *
 * `fallbackLng: 'en'` est une exigence explicite de la spec : un navigateur dans
 * une langue non supportée retombe sur l'anglais, jamais sur le français.
 * Un choix explicite de l'utilisateur (`i18n.changeLanguage`, via
 * `LanguageSwitcher`) est mis en cache dans `localStorage` et prime sur la
 * détection navigateur au prochain chargement (ordre de détection ci-dessous).
 *
 * `createI18nOptions()` renvoie un objet NEUF à chaque appel : `i18next.init()`
 * mute l'objet d'options qu'on lui passe (il y ajoute `cimode`, normalise
 * `fallbackLng` en tableau…) — un export figé serait corrompu dès le premier
 * `init()` et donnerait des assertions fausses en test.
 */
export function createI18nOptions() {
  return {
    resources: {
      en: { translation: en },
      fr: { translation: fr },
    },
    fallbackLng: 'en',
    // Depuis le contrat partagé : c'est la même liste qui borne `users.locale` côté serveur.
    // Une langue acceptée en base et absente d'ici afficherait des clés brutes.
    supportedLngs: [...LOCALES],
    load: 'languageOnly', // fr-FR / en-GB → fr / en
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
  };
}

i18next.use(LanguageDetector).use(initReactI18next).init(createI18nOptions());

// `document.documentElement.lang` doit suivre la langue active — posé à
// l'initialisation et à chaque changement explicite (LanguageSwitcher).
if (typeof document !== 'undefined') {
  i18next.on('languageChanged', (langue) => {
    document.documentElement.lang = langue;
  });
  if (i18next.language) {
    document.documentElement.lang = i18next.language;
  }
}

export default i18next;
